import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, GitBranch, Plus, Trash2 } from "lucide-react";

/**
 * PO Amendment modal — lifts a PO out of the "raise a fresh one" cost by
 * letting admins bump lines / rate / tax on an existing sent-or-approved PO.
 * Every save is snapshotted in db.po_amendments and audit-logged with the
 * mandatory reason.
 */
export default function POAmendmentModal({ po, onCancel, onSaved }) {
  const [reason, setReason] = useState("");
  const [taxPercent, setTaxPercent] = useState(po.tax_percent || 0);
  const [lines, setLines] = useState(
    (po.lines || []).map((l) => ({
      item_id: l.item_id || "",
      item_name: l.item_name || "",
      unit: l.unit || "",
      qty_ordered: Number(l.qty_ordered || 0),
      rate: Number(l.rate || 0),
      category: l.category || "",
    })),
  );
  const [busy, setBusy] = useState(false);

  const updateLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines([...lines, { item_id: "", item_name: "", unit: "kg", qty_ordered: 0, rate: 0, category: "" }]);
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const subtotal = lines.reduce((s, l) => s + Number(l.qty_ordered || 0) * Number(l.rate || 0), 0);
  const taxAmount = Math.round(subtotal * Number(taxPercent || 0) * 100 / 100) / 100;
  const total = Math.round((subtotal + taxAmount) * 100) / 100;
  const priorTotal = Number(po.total || 0);
  const diff = total - priorTotal;
  const diffPct = priorTotal ? (diff / priorTotal) * 100 : 0;

  const submit = async () => {
    if (reason.trim().length < 5) { toast.error("Amendment reason (min 5 chars) is required"); return; }
    if (!lines.length) { toast.error("At least one line required"); return; }
    setBusy(true);
    try {
      const payload = {
        reason: reason.trim(),
        new_lines: lines.map((l) => ({
          ...l,
          qty_ordered: Number(l.qty_ordered || 0),
          rate: Number(l.rate || 0),
          qty_received: 0,
          amount: 0,
        })),
        new_tax_percent: Number(taxPercent || 0),
      };
      await api.post(`/staff/purchase-orders/${po.id}/amendments`, payload);
      toast.success("PO amended · audit log updated");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Amendment failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="po-amendment-modal"
         className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--rule))] w-full max-w-4xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="eyebrow flex items-center gap-2"><GitBranch size={12} /> PO amendment</div>
            <h2 className="mt-1 display text-xl">Revise {po.po_number}</h2>
            <p className="mt-1 text-xs text-[hsl(var(--ink-soft))]">
              Vendor: <span className="font-semibold">{po.vendor_snapshot?.name}</span> · Prior total: <span className="font-mono">₹{priorTotal.toLocaleString("en-IN")}</span>
            </p>
          </div>
          <button onClick={onCancel} className="text-[hsl(var(--ink-soft))]"><X size={18} /></button>
        </div>

        <label className="block mt-5">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--ink-soft))] mb-1">
            Reason for amendment * (min 5 chars — kept in audit trail)
          </span>
          <textarea
            data-testid="po-amend-reason"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Vendor added Bread ₹200 confirmed on WhatsApp"
            className="tbb-inp w-full resize-none text-sm"
            autoFocus
          />
        </label>

        <div className="mt-5 border border-[hsl(var(--rule))]">
          <div className="p-2 flex items-center justify-between bg-[hsl(var(--bg-2))]">
            <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Lines</div>
            <button onClick={addLine} data-testid="po-amend-add-line"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[hsl(var(--rule))]">
              <Plus size={12} /> Add line
            </button>
          </div>
          <table className="w-full text-xs">
            <thead className="text-[hsl(var(--ink-soft))]">
              <tr>
                <th className="text-left p-2">Item</th>
                <th className="text-right p-2 w-20">Unit</th>
                <th className="text-right p-2 w-24">Qty</th>
                <th className="text-right p-2 w-24">Rate ₹</th>
                <th className="text-right p-2 w-28">Amount</th>
                <th className="p-2 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--rule))]">
              {lines.map((l, i) => (
                <tr key={i} data-testid={`po-amend-line-${i}`}>
                  <td className="p-1"><input value={l.item_name} onChange={(e) => updateLine(i, { item_name: e.target.value })}
                                              data-testid={`po-amend-line-item-${i}`}
                                              className="tbb-inp w-full text-xs !py-1" /></td>
                  <td className="p-1"><input value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })}
                                              className="tbb-inp w-full text-xs !py-1 text-right" /></td>
                  <td className="p-1"><input type="number" step="0.01" min="0" value={l.qty_ordered}
                                               data-testid={`po-amend-line-qty-${i}`}
                                               onChange={(e) => updateLine(i, { qty_ordered: e.target.value })}
                                               className="tbb-inp w-full text-xs !py-1 text-right" /></td>
                  <td className="p-1"><input type="number" step="0.01" min="0" value={l.rate}
                                               data-testid={`po-amend-line-rate-${i}`}
                                               onChange={(e) => updateLine(i, { rate: e.target.value })}
                                               className="tbb-inp w-full text-xs !py-1 text-right" /></td>
                  <td className="p-2 text-right font-mono">₹{(Number(l.qty_ordered) * Number(l.rate) || 0).toFixed(2)}</td>
                  <td className="p-1 text-right">
                    <button onClick={() => removeLine(i)} className="text-[hsl(6_62%_45%)]"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
          <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            Tax %
            <input type="number" step="0.01" min="0" value={taxPercent}
                   onChange={(e) => setTaxPercent(e.target.value)}
                   className="tbb-inp mt-1 w-32" />
          </label>
          <div className="text-right text-sm">
            <div>Subtotal: <span className="font-mono">₹{subtotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            <div>Tax: <span className="font-mono">₹{taxAmount.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            <div className="mt-1 text-lg">New total: <span className="font-mono font-semibold">₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span></div>
            {diff !== 0 && (
              <div className={`text-xs mt-1 ${diff > 0 ? "text-[hsl(35_75%_38%)]" : "text-[hsl(148_40%_35%)]"}`}>
                {diff > 0 ? "↑" : "↓"} ₹{Math.abs(diff).toFixed(2)} ({diffPct >= 0 ? "+" : ""}{diffPct.toFixed(1)}%) vs prior
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onCancel} className="text-sm text-[hsl(var(--ink-soft))] px-3">Cancel</button>
          <button
            data-testid="po-amend-submit"
            onClick={submit}
            disabled={busy || reason.trim().length < 5}
            className="btn-brass !py-2.5 !px-5 text-sm disabled:opacity-50"
          >
            {busy ? "Amending…" : "Save amendment"}
          </button>
        </div>
        <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
      </div>
    </div>
  );
}
